import { ReactElement, ReactNode } from 'react';

import Plan from 'models/Plan';
import type { Extras, PlanName } from 'models/Plan';

import Modal from 'components/shared/Modal';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';

import noPreferenceUrl from 'consumer/signup/plans/meal_preference/chefs_choice--xxl@1x.png';
import noPreferenceUrl2x from 'consumer/signup/plans/meal_preference/chefs_choice--xxl@2x.png';
import noPreferenceUrlxs1 from 'consumer/signup/plans/meal_preference/chefs_choice--xxs@1x.png';
import noPreferenceUrlxs2 from 'consumer/signup/plans/meal_preference/chefs_choice--xxs@2x.png';

import highProteinUrl from 'consumer/signup/plans/meal_preference/high_protein--xxl@1x.png';
import highProteinUrl2x from 'consumer/signup/plans/meal_preference/high_protein--xxl@2x.png';
import highProteinUrlxs1 from 'consumer/signup/plans/meal_preference/high_protein--xxs@1x.png';
import highProteinUrlxs2 from 'consumer/signup/plans/meal_preference/high_protein--xxs@2x.png';

import quickEasyUrl from 'consumer/signup/plans/meal_preference/quick_easy--xxl@1x.png';
import quickEasyUrl2x from 'consumer/signup/plans/meal_preference/quick_easy--xxl@2x.png';
import quickEasyUrlxs1 from 'consumer/signup/plans/meal_preference/quick_easy--xxs@1x.png';
import quickEasyUrlxs2 from 'consumer/signup/plans/meal_preference/quick_easy--xxs@2x.png';

import glutenFreeUrl from 'consumer/signup/plans/meal_preference/gluten_free--xxl@1x.png';
import glutenFreeUrl2x from 'consumer/signup/plans/meal_preference/gluten_free--xxl@2x.png';
import glutenFreeUrlxs1 from 'consumer/signup/plans/meal_preference/gluten_free--xxs@1x.png';
import glutenFreeUrlxs2 from 'consumer/signup/plans/meal_preference/gluten_free--xxs@2x.png';

import MealPreferenceCard from '../cards/MealPreferenceCard';

type PlanList = {
  id: PlanName;
  label: ReactNode;
  detail: string;
  imgArray: Record<string, string>;
};

type Props = {
  show: boolean;
  plan: Plan;
  extras?: Extras;
  setPlan: (plan: Plan) => void;
  onClose: () => void;
  onConfirm: () => void;
};
const list: PlanList[] = [
  {
    id: 'chefs_choice',
    label: 'No Preference',
    detail: 'Not ready to decide? Pick your preferred meals another time.',
    imgArray: {
      lg_1x: noPreferenceUrl,
      lg_2x: noPreferenceUrl2x,
      sm_1x: noPreferenceUrlxs1,
      sm_2x: noPreferenceUrlxs2,
    },
  },
  {
    id: 'high_protein',
    label: 'High Protein',
    detail: 'Delicious plant-based meals with at least 20 grams of protein.',
    imgArray: {
      lg_1x: highProteinUrl,
      lg_2x: highProteinUrl2x,
      sm_1x: highProteinUrlxs1,
      sm_2x: highProteinUrlxs2,
    },
  },
  {
    id: 'quick_easy',
    label: <abbr>Less Prep</abbr>,
    detail: 'Reduce prep time and use fewer pots and pans.',
    imgArray: {
      lg_1x: quickEasyUrl,
      lg_2x: quickEasyUrl2x,
      sm_1x: quickEasyUrlxs1,
      sm_2x: quickEasyUrlxs2,
    },
  },
  {
    id: 'gluten_free',
    label: 'Gluten Free',
    detail: 'Amazing meals with tons of flavor and no gluten.',
    imgArray: {
      lg_1x: glutenFreeUrl,
      lg_2x: glutenFreeUrl2x,
      sm_1x: glutenFreeUrlxs1,
      sm_2x: glutenFreeUrlxs2,
    },
  },
];

function MealPreferencesModal({
  show,
  plan,
  extras,
  setPlan,
  onClose,
  onConfirm,
}: Props): ReactElement {
  return (
    <Modal
      show={show}
      onClose={onClose}
      className="c-modal-meal-preferences-new"
      body={(
        <>
          <button
            className="close"
            type="button"
            aria-label="Close modal"
            data-cy="close-modal"
            onClick={onClose}
          >
            <CloseIcon width="20" height="20" />
          </button>

          <div
            className={[
              'c-modal-meal-preferences-new__title',
              'c-heading',
              'c-heading--brand-4',
            ].join(' ')}
          >
            What type of meals&nbsp;
            <br className="d-sm-none" />
            do you prefer?
          </div>

          <p className="u-text u-text--sm u-text--center mb-md-2 mt-0 mt-md-3">
            Let us know your top pick so we can give you more of what you love.&nbsp;
            <br className="d-none d-sm-inline" />
            And don&lsquo;t worry, you still get to customize your order every week.
          </p>

          <div className="c-modal-meal-preferences-new__cards_container">
            {list.map((item) => (
              <MealPreferenceCard
                key={item.id}
                id={item.id}
                details={item.detail}
                mealPrep={item.label}
                isSelected={plan.name === item.id}
                handlePlanSelect={() => setPlan(new Plan(item.id, extras))}
                images={item.imgArray}
              />
            ))}
          </div>
          <div className="u-text--center">
            <button
              type="button"
              className="c-button c-button--primary"
              onClick={() => onConfirm()}
              data-cy="submit-meal-preference"
            >
              Save Preference
            </button>
          </div>
        </>
      )}
    />
  );
}

export default MealPreferencesModal;
